/**
 * Customer-facing frontier models shown in the signed-in user menu.
 *
 * - `hasLiveAccess: true` routes straight into the live product (Guardian chat).
 * - `invitable: true` opens a description page with a "Get Invited to our main app" CTA
 *   (the model is production-ready in the Interlinked platform).
 * - otherwise the model is in development: description page only, no invite.
 */
export const FRONTIER_MODELS = [
  {
    id: 'supervised-roi',
    label: 'Risk Of Ignition',
    title: 'Supervised Risk of Ignition',
    inviteName: 'Risk Of Ignition',
    hasLiveAccess: false,
    invitable: true,
    family: 'Predictive wildfire risk',
    status: 'Production',
    statusColor: 'success',
    summary: [
      'The production RoI ensemble estimates ignition risk for a location.',
      'It converts current environmental conditions into an operational score from 0–100.',
    ],
    decides: [
      'How likely current environmental conditions are to support wildfire ignition.',
      'The prediction is specific to the coordinates supplied by the operator.',
    ],
    output: [
      'A risk score from 0–100, risk class, and confidence.',
      'The response also includes Weather Danger Index (WDI), cluster assignment, and component-model scores.',
    ],
    howItWorks: [
      'Collects and scales roughly 81 weather, vegetation, terrain, and environmental features.',
      'Compresses those features with an autoencoder and assigns the location to a fire-potential cluster.',
      'Runs three supervised predictors: a neural-network MLP, a gradient-boosted tree model, and K-nearest neighbors.',
      'Combines and calibrates their predictions through a stacking model, then maps the result to WDI and a risk class.',
    ],
    notes: [
      'This is the only RoI model currently serving client requests end to end.',
      'Guardian chat and specialist agents can call this model when operators ask for ignition risk at a location.',
    ],
  },
  {
    id: 'stdt-foundation',
    label: 'StDT Foundation',
    title: 'StDT Foundation Engine',
    inviteName: 'StDT Foundation',
    hasLiveAccess: false,
    invitable: false,
    family: 'Predictive wildfire risk',
    status: 'In development',
    statusColor: 'warning',
    summary: [
      'A next-generation spatiotemporal foundation engine for wildfire ignition risk.',
      'It is designed to replace the current supervised RoI scorer with richer weather and Earth-observation context.',
    ],
    decides: [
      'Ignition risk over space and time.',
      'The model combines multi-modal weather, Earth-observation, and physics signals.',
    ],
    output: [
      'Ignition risk estimates produced by a multi-model ensemble.',
      'The ensemble includes LightGBM, a spatiotemporal deep model, a physics classifier, and a deterministic foundation scorer.',
    ],
    howItWorks: [
      'Combines modality-specific encoders with cross-modal fusion for spatiotemporal risk estimation.',
      'Uses frozen foundation encoders for weather and Earth observation context, including ClimaX, Earthformer, and Prithvi.',
      'Keeps a physics-based classifier in observable, auditable feature space.',
      'Includes a deterministic foundation scorer that blends hazard and Fire Weather Index into operational risk buckets.',
    ],
    notes: [
      'This is the intended next-generation replacement for the live supervised RoI ensemble.',
      'Invite access is for early evaluation and roadmap visibility, not live production scoring.',
    ],
  },
  {
    id: 'aurora-rl',
    label: 'Aurora RL',
    title: 'Aurora Decision Intelligence',
    inviteName: 'Aurora RL',
    hasLiveAccess: false,
    invitable: false,
    family: 'Reinforcement learning / active sensing',
    status: 'In development',
    statusColor: 'warning',
    summary: [
      'Aurora allocates a limited scoring budget across evacuation zones.',
      'It does not score ignition risk itself. It decides where fresh risk computation is most valuable.',
    ],
    decides: [
      'Which zones should be rescored first and how frequently they should refresh.',
      'It also determines how densely each evacuation-zone polygon should be sampled.',
    ],
    output: [
      'A criticality tier from 1–5 for each zone.',
      'That tier drives refresh cadence and in-polygon sampling density.',
    ],
    howItWorks: [
      'Models zones as a restless multi-armed bandit and ranks them with an interpretable Whittle index.',
      'Optimizes value of information minus compute cost and service-level-objective breach penalties.',
      'Applies deterministic safety floors, budget allocation, hysteresis, and fairness guardrails after policy ranking.',
      'Keeps the learned policy responsible for ranking only; guardrails determine what is safe and affordable.',
    ],
    notes: [
      'Offline evaluation reported stronger reward than the current percentile heuristic.',
      'Invite access is for early customers and partners evaluating active-sensing workflows.',
    ],
  },
  {
    id: 'guardian',
    label: 'Guardian',
    title: 'Guardian',
    inviteName: 'Guardian',
    hasLiveAccess: true,
    family: 'Conversational AI',
    status: 'Production',
    statusColor: 'success',
    summary: [
      'Guardian is the conversational interface to Interlinked intelligence and operations.',
      'A LangGraph supervisor routes each request to a specialist that can explain risk, create incidents, write reports, and recommend tactics.',
    ],
    decides: [
      'Which specialist should handle an operator request.',
      'The selected specialist then determines which tools to call to answer safely and accurately.',
    ],
    output: [
      'Streaming conversational responses grounded in Interlinked services.',
      'Tool-backed actions include RoI lookup, incident creation, reporting, and tactic recommendations.',
    ],
    howItWorks: [
      'A temperature-0 supervisor selects one specialist per turn.',
      'Specialists include general, orchestrator, RoI, incident, reporting, and tactics advisors.',
      'Agents call Interlinked Backend and environmental data services through registered tools.',
      'Responses stream over the Guardian WebSocket chat interface.',
    ],
    notes: [
      'This is the customer-facing conversational layer over live Interlinked services.',
      'RoI questions can use the production supervised RoI endpoint when coordinates are available.',
    ],
  },
];

/**
 * Resolves a menu/view id to frontier model metadata.
 * @param {string} id - e.g. `supervised-roi`, `guardian`.
 * @returns {object|null}
 */
export function getFrontierModel(id) {
  return FRONTIER_MODELS.find((model) => model.id === id) || null;
}
